import { ContractEngine } from '../contract/engine.js';
import { GovernanceDecisionValue } from '../contract/constants.js';
import { buildAuditEntry, buildDetailedTrace } from '../audit/exports.js';
import { stableSerialize } from '../contract/stable.js';

const freeze = (value) => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(freeze);
    Object.freeze(value);
  }
  return value;
};

export class IncrementalExecutionController {
  #scenario;
  #engine;
  #commands = [];
  #position = 0;
  #snapshot;
  #replayExpected = null;
  #replayVerified = null;

  constructor(scenario) { this.loadScenario(scenario); }

  loadScenario(scenario) {
    if (!scenario) throw new Error('SCENARIO_REQUIRED');
    this.#scenario = scenario;
    this.#replayExpected = null;
    this.#replayVerified = null;
    return this.reset();
  }

  reset({ preserveReplayExpectation = false } = {}) {
    if (!preserveReplayExpectation) {
      this.#replayExpected = null;
      this.#replayVerified = null;
    }
    this.#engine = new ContractEngine({
      scenarioId: this.#scenario.scenarioId,
      stabilizationRequirement: this.#scenario.configuration.stabilizationRequirement,
      initialRegime: this.#scenario.regime.status,
      stableSteps: this.#scenario.regime.stableSteps
    });
    this.#commands = this.#buildCommands();
    this.#position = 0;
    this.#snapshot = this.#buildSnapshot();
    return this.#snapshot;
  }

  replay() {
    this.#replayExpected = this.isComplete() ? this.#completionArtifact() : null;
    this.#replayVerified = null;
    return this.reset({ preserveReplayExpectation: true });
  }

  step() {
    if (this.isComplete()) return this.#snapshot;
    const before = this.#engine.getState().timeline.length;
    this.#execute(this.#commands[this.#position]);
    const after = this.#engine.getState().timeline.length;
    if (after !== before + 1) throw new Error(`NON_ATOMIC_PLAYBACK_STEP: expected 1 transition, received ${after - before}`);
    this.#position += 1;
    if (this.isComplete() && this.#replayExpected !== null) {
      if (this.#completionArtifact() !== this.#replayExpected) throw new Error('DETERMINISTIC_REPLAY_MISMATCH');
      this.#replayVerified = true;
    }
    this.#snapshot = this.#buildSnapshot();
    return this.#snapshot;
  }

  runToCompletion() { while (!this.isComplete()) this.step(); return this.#snapshot; }
  getSnapshot() { return this.#snapshot; }
  getTotalSteps() { return this.#commands.length; }
  isComplete() { return this.#position >= this.#commands.length; }

  #buildCommands() {
    const commands = [];
    this.#scenario.orderedEvents.forEach((event, index) => {
      if (this.#scenario.scenarioId === 'recall-self-promotion' && index === 1) {
        commands.push({ type: 'recall', timestamp: event.timestamp });
        commands.push({ type: 'request', requestId: 'request-recall-direct', requestedAt: event.timestamp, directRecallAttempt: true });
        commands.push({ type: 'evaluate', requestId: 'request-recall-direct' });
        commands.push({ type: 'govern', requestId: 'request-recall-direct', options: {
          requestedDecision: GovernanceDecisionValue.ALLOW,
          rationale: 'Recall is attention-only and cannot directly authorize durable state.'
        } });
      }
      commands.push({ type: 'receive', event, evidence: this.#scenario.evidence[index] });
      commands.push({ type: 'admit', eventId: event.eventId });
      commands.push({ type: 'associate', eventId: event.eventId });
    });

    const requestId = `request-${this.#scenario.scenarioId}`;
    commands.push({ type: 'request', requestId, requestedAt: '2026-01-15T13:00:00.000Z' });
    commands.push({ type: 'evaluate', requestId });
    if (this.#scenario.scenarioId === 'instability-freeze') {
      commands.push({ type: 'govern', requestId, options: this.#scenario.governance.initial });
      commands.push({ type: 'beginStabilization' });
      for (let i = 0; i < this.#scenario.configuration.stabilizationRequirement; i += 1) commands.push({ type: 'stabilize' });
      commands.push({ type: 'request', requestId: 'request-instability-resumed', requestedAt: '2026-01-15T13:06:00.000Z' });
      commands.push({ type: 'evaluate', requestId: 'request-instability-resumed' });
      commands.push({ type: 'govern', requestId: 'request-instability-resumed', options: this.#scenario.governance.resumed });
      commands.push({ type: 'commit', requestId: 'request-instability-resumed' });
    } else {
      commands.push({ type: 'govern', requestId, options: this.#scenario.governance });
      if (this.#scenario.governance.requestedDecision === GovernanceDecisionValue.ALLOW
        && this.#scenario.governance.governanceToken
        && this.#scenario.governance.signer) commands.push({ type: 'commit', requestId });
    }
    return freeze(commands);
  }

  #execute(command) {
    switch (command.type) {
      case 'receive': this.#engine.receiveEvent(command.event, command.evidence); break;
      case 'admit': this.#engine.admitReceivedEvent(command.eventId); break;
      case 'associate': this.#engine.associateAdmittedEvent(command.eventId); break;
      case 'recall': this.#engine.recall(this.#engine.getState().traces[0].traceId, command.timestamp); break;
      case 'request': this.#engine.requestPromotion({ ...command, type: undefined, deferPredicates: true }); break;
      case 'evaluate': this.#engine.evaluatePromotion(command.requestId); break;
      case 'govern': this.#engine.govern(command.requestId, { ...command.options, deferCommit: true }); break;
      case 'beginStabilization': this.#engine.beginStabilization(); break;
      case 'stabilize': this.#engine.stabilizationStep(); break;
      case 'commit': this.#engine.commitAuthorized(command.requestId); break;
      default: throw new Error(`EXECUTION_COMMAND_UNKNOWN: ${command.type}`);
    }
  }

  #completionArtifact() {
    const state = this.#engine.getState();
    return stableSerialize({
      state,
      audit: buildAuditEntry(this.#scenario, state),
      trace: buildDetailedTrace(this.#scenario, state)
    });
  }

  #buildSnapshot() {
    const state = this.#engine.getState();
    const complete = this.isComplete();
    const auditEntries = complete ? [buildAuditEntry(this.#scenario, state)] : [];
    return freeze({
      scenarioId: this.#scenario.scenarioId,
      sequence: state.timeline.length,
      complete,
      eventsVisible: state.events,
      associativeTraces: state.traces,
      promotionRequests: state.requests,
      governanceDecisions: state.decisions,
      quarantineRecords: state.quarantineRecords,
      canonicalRecords: state.canonicalRecords,
      transitionRecords: state.transitionRecords,
      regimeStatus: state.regime,
      auditEntries,
      state,
      trace: buildDetailedTrace(this.#scenario, state),
      replayVerified: this.#replayVerified
    });
  }
}
